import {
  Alert,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Typography,
  notification,
} from 'antd';
import { PlayCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { RUN_CODE_GROUPED_OPTIONS } from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { RunCode, RunResult } from 'pages/star-shared/types';
import { formatNumber } from 'pages/star-shared/utils';

type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface FormValue {
  code: RunCode;
  periodKey?: string;
  range?: RangeValue;
}

// Mỗi mã chạy nhận một kiểu kỳ (SPEC-NHIEM-VU-MO-RONG.md §2.2): mã tuần W…, mã tháng M…,
// khoảng ngày, hoặc không cần gì. Ô không dùng tới bị khoá để khỏi nhập nhầm.
type RunInput = 'week' | 'month' | 'range' | 'none';

const RUN_INPUT: Record<RunCode, { input: RunInput; hint: string; maxDays?: number }> = {
  ATTENDANCE_WEEK: { input: 'week', hint: 'Mã tuần, VD: W2026-39. Bỏ trống = tuần vừa rồi.' },
  KC_DAILY: { input: 'range', hint: 'Chọn khoảng ngày KC cần chấm lại.' },
  KC_DAYS_MONTH: { input: 'month', hint: 'Mã tháng, VD: M2026-09. Bỏ trống = tháng vừa rồi.' },
  MONTH_TOPUP: { input: 'month', hint: 'Mã tháng, VD: M2026-09. Bỏ trống = tháng vừa rồi.' },
  SHIFT_ON_TIME: {
    input: 'range',
    hint: 'Khoảng ngày VN, tối đa 62 ngày. Bỏ trống = 3 ngày gần nhất. Mỗi ngày chỉ trả một lần.',
    maxDays: 62,
  },
  MONTH_PERFECT: { input: 'month', hint: 'Mã tháng, VD: M2026-09. Bỏ trống = tháng trước.' },
  TENURE: { input: 'none', hint: 'Không cần kỳ: xét mọi Nhân viên đã tới mốc thâm niên.' },
  RECRUIT: {
    input: 'none',
    hint: 'Không cần kỳ: chấm mọi phiếu tuyển dụng đang theo dõi và trả các mốc đã đạt (trong trần tháng).',
  },
  LIVE_WEEK: {
    input: 'week',
    hint: 'Mã tuần theo ngày TikTok, VD: W2026-39. Bỏ trống = tuần vừa khép.',
  },
  LIVE_MONTH: {
    input: 'month',
    hint: 'Mã tháng theo ngày TikTok, VD: M2026-09. Bỏ trống = tháng trước.',
  },
  KC_GROWTH: {
    input: 'month',
    hint: 'Mã tháng theo ngày TikTok, VD: M2026-09. Bỏ trống = tháng trước. Chấm lại chỉ trả phần chênh.',
  },
};

// Placeholder ngắn cho vừa ô (~190px); giải thích đầy đủ hiện dưới hàng ô nhập.
const PERIOD_PLACEHOLDER: Record<RunInput, string> = {
  week: 'VD: W2026-39',
  month: 'VD: M2026-09',
  range: 'Không cần',
  none: 'Không cần',
};

const PERIOD_PATTERN: Partial<Record<RunInput, RegExp>> = {
  week: /^W\d{4}-\d{2}$/,
  month: /^M\d{4}-(0[1-9]|1[0-2])$/,
};

// "Chạy chấm" thủ công: POST /stars/admin/missions/run — dùng khi cron tắt (dev) hoặc cần chấm lại.
const RunMissionCard = () => {
  const [form] = Form.useForm<FormValue>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const code = Form.useWatch('code', form) as RunCode | undefined;
  const spec = code ? RUN_INPUT[code] : undefined;
  const input: RunInput = spec?.input || 'none';

  const run = async () => {
    const values = await form.validateFields();
    const kind = RUN_INPUT[values.code]?.input || 'none';
    const payload: Record<string, any> = { code: values.code };
    if ((kind === 'week' || kind === 'month') && values.periodKey?.trim()) {
      payload.periodKey = values.periodKey.trim().toUpperCase();
    }
    if (kind === 'range') {
      if (values.range?.[0]) payload.from = dayjs(values.range[0]).format('YYYY-MM-DD');
      if (values.range?.[1]) payload.to = dayjs(values.range[1]).format('YYYY-MM-DD');
    }
    setLoading(true);
    setResult(null);
    try {
      const res: any = await AxiosUtils.post(API.starsMissionRun, payload);
      const data: RunResult = res?.data || { evaluated: 0, rewarded: 0, stars: 0 };
      setResult(data);
      notification.success({ message: 'Đã chạy chấm.' });
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      size="small"
      title="Chạy chấm thủ công"
      styles={{ body: { padding: 16 } }}
      style={{ marginBottom: 16 }}
    >
      <Form
        form={form}
        layout="vertical"
        className="custom-form"
        initialValues={{ code: 'ATTENDANCE_WEEK' }}
      >
        <Row gutter={[16, 8]} align="bottom">
          <Col xl={8} lg={10} md={12} xs={24}>
            <Form.Item
              label="Nhiệm vụ"
              name="code"
              rules={[{ required: true, message: 'Chọn nhiệm vụ.' }]}
            >
              <Select
                options={RUN_CODE_GROUPED_OPTIONS}
                onChange={() => form.setFieldsValue({ periodKey: undefined, range: undefined })}
              />
            </Form.Item>
          </Col>
          <Col xl={5} lg={6} md={12} xs={24}>
            <Form.Item
              label="Kỳ"
              name="periodKey"
              tooltip={spec?.hint}
              normalize={(value?: string) => value?.toUpperCase()}
              rules={[
                {
                  validator: (_, value?: string) => {
                    const pattern = PERIOD_PATTERN[input];
                    if (!pattern || !value?.trim() || pattern.test(value.trim())) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(input === 'week' ? 'Dạng W2026-39.' : 'Dạng M2026-09.')
                    );
                  },
                },
              ]}
            >
              <Input
                placeholder={PERIOD_PLACEHOLDER[input]}
                maxLength={16}
                disabled={input !== 'week' && input !== 'month'}
              />
            </Form.Item>
          </Col>
          <Col xl={7} lg={8} md={12} xs={24}>
            <Form.Item
              label="Khoảng ngày"
              name="range"
              rules={[
                {
                  validator: (_, value?: RangeValue) => {
                    const maxDays = spec?.maxDays;
                    if (!maxDays || !value?.[0] || !value?.[1]) return Promise.resolve();
                    return dayjs(value[1]).diff(dayjs(value[0]), 'day') + 1 <= maxDays
                      ? Promise.resolve()
                      : Promise.reject(new Error(`Tối đa ${maxDays} ngày.`));
                  },
                },
              ]}
            >
              <DatePicker.RangePicker
                format="DD/MM/YYYY"
                placeholder={['Từ', 'Đến']}
                allowEmpty={[true, true]}
                disabled={input !== 'range'}
              />
            </Form.Item>
          </Col>
          <Col xl={4} lg={24} md={12} xs={24}>
            <Form.Item label=" ">
              <Popconfirm
                title="Chạy chấm ngay?"
                description="Sao sẽ được cộng thật cho người đạt. Chạy lại cùng kỳ không cộng trùng."
                okText="Chạy"
                cancelText="Huỷ"
                onConfirm={run}
              >
                {/* CustomButton không nhận ref: bọc span để Popconfirm không phải dùng findDOMNode. */}
                <span>
                  <CustomButton
                    bgColor="primary-orange"
                    text="Chạy chấm"
                    icon={<PlayCircleOutlined />}
                    loading={loading}
                  />
                </span>
              </Popconfirm>
            </Form.Item>
          </Col>
        </Row>
        {spec && (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: -8 }}>
            {spec.hint}
          </Typography.Text>
        )}
      </Form>
      {result && (
        <Alert
          type="success"
          showIcon
          message={`Đã xét ${formatNumber(result.evaluated)} người · thưởng ${formatNumber(
            result.rewarded
          )} lượt · tổng ${formatNumber(result.stars)} ⭐`}
        />
      )}
    </Card>
  );
};

export default RunMissionCard;
